// Zod mirrors of the motion primitive (docs/plans/v2-motion-primitive.md §2).
//
// These live in their OWN module rather than in `schemas.ts` because `theme.ts`
// needs `motionTrackSchema` for its per-token override (§6) and `schemas.ts`
// already imports `theme.ts` — putting them together would be a runtime import
// cycle between two modules of zod values.
//
// `schemas.ts` re-exports every symbol here, so nothing outside this package
// has to know the file moved.
import { z } from 'zod';
import { EASING_REFS, EXIT_TOKENS, MOTION_CHANNELS } from './motion.js';
import { animationRef } from './registries/index.js';
// The composable primitive underneath the 12 animation tokens. Every field is
// bounded and numeric so an LLM can emit it and the gate can reject it — no
// CSS strings, no bezier coefficients, no raw spring configs.
export const motionChannelSchema = z.enum(MOTION_CHANNELS);
export const easingRefSchema = z.enum(EASING_REFS);
export const motionAnchorSchema = z.discriminatedUnion('at', [
    z.object({
        at: z.literal('start'),
        frame: z.number().int().min(0).optional(),
    }),
    z.object({ at: z.literal('end'), frame: z.number().int().min(0).optional() }),
]);
// A shaped mask part. Every field is a bounded 0–1 fraction of the element
// box, so the gate can reject a nonsense shape the same way it rejects an
// opacity of 3 — which is the entire reason this is a closed shape set and not
// a `clipPath` string.
//
// The bounds are deliberately [0,1] and not wider: a shape that starts wholly
// outside the box is expressible as a zero-size shape at the edge (which is
// what `corner-l-wipe`'s rects do), and every real off-box start we found is
// that. If a mask ever needs to travel in from genuinely outside, the bound
// moves in a PR where a human looks at a render.
// A mask carries TWO kinds of number and they have different domains.
//
// POSITION (cx/cy, x/y) is a point in the element box, so 0-1 is the whole
// meaningful range and clamping is correct.
//
// EXTENT (rx/ry, w/h) is a SIZE, and a mask's entire job is to leave the frame.
// Capping extent at 1 makes a fully-open feathered mask unrepresentable: the
// soft edge cannot be carried off-frame, so it parks inside it and the corners
// stay permanently darkened. Measured, an iris capped this way left the frame
// corners at 48% brightness for ever. `rx: 2` was silently clamped to 1 and
// every experiment after that measured the same clamped shape.
//
// The cap is 8 rather than unbounded so a typo is still an error: eight times
// the frame is far past any real transition, and the schema should still catch
// a stray `rx: 200`.
const maskPosition = z.number().min(0).max(1);
const maskExtent = z.number().min(0).max(8);
export const motionMaskEllipseSchema = z.object({
    cx: maskPosition.optional(),
    cy: maskPosition.optional(),
    rx: maskExtent,
    ry: maskExtent,
});
// The superellipse EXPONENT: |x/rx|^n + |y/ry|^n = 1.
//
// n=2 is exactly an ellipse, n=1 a diamond, and n→∞ a rectangle (n=8 already
// reads as a rounded rect, n=16 as a rect with a hint of corner radius). It is
// the one number that makes oval→rectangle a MORPH rather than two shapes
// cross-fading, which the discriminated union cannot express — `from`/`to` must
// name the same shape, deliberately, so that a rect→ellipse morph with no
// meaning does not typecheck.
//
// Capped at 16 because past that the sampled path is indistinguishable from a
// rect at this viewBox and the only thing that changes is how badly uniform
// parameter sampling resolves the corners.
const maskExponent = z.number().min(1).max(16);
export const motionMaskSuperellipseSchema = z.object({
    cx: maskPosition.optional(),
    cy: maskPosition.optional(),
    rx: maskExtent,
    ry: maskExtent,
    /** Default 2, i.e. an ellipse. */
    n: maskExponent.optional(),
});
export const motionMaskRectSchema = z.object({
    // A rect's x/y may sit OUTSIDE the box (a wipe starts off-frame and travels
    // in), so position here is an extent-like signed offset rather than a point.
    x: z.number().min(-8).max(8).optional(),
    y: z.number().min(-8).max(8).optional(),
    w: maskExtent,
    h: maskExtent,
    // Degrees about the element-box centre. Bounded at a half-turn each way
    // because a rect at +190° is the same rect at -170° and two spellings of one
    // shape is the padding this registry keeps having to remove.
    rotate: z.number().min(-180).max(180).optional(),
    // Flat alpha inside the shape. Distinct from `feather`, which is a gradient
    // at the boundary — see MotionMaskRect.
    alpha: z.number().min(0).max(1).optional(),
});
export const motionMaskPartSchema = z.discriminatedUnion('shape', [
    z.object({
        shape: z.literal('ellipse'),
        from: motionMaskEllipseSchema,
        to: motionMaskEllipseSchema,
        feather: z.number().min(0).max(1).optional(),
        mode: z.enum(['sweep', 'fade']).optional(),
        lag: z.number().min(0).max(1).optional(),
    }),
    z.object({
        shape: z.literal('superellipse'),
        from: motionMaskSuperellipseSchema,
        to: motionMaskSuperellipseSchema,
        feather: z.number().min(0).max(1).optional(),
    }),
    z.object({
        shape: z.literal('rect'),
        from: motionMaskRectSchema,
        to: motionMaskRectSchema,
        feather: z.number().min(0).max(1).optional(),
    }),
]);
export const motionTrackSchema = z.object({
    channel: motionChannelSchema,
    from: z.number().finite(),
    to: z.number().finite(),
    anchor: motionAnchorSchema.optional(),
    duration: z.union([z.literal('block'), z.number().int().positive()]),
    easing: easingRefSchema.optional(),
    repeat: z
        .object({
        mode: z.enum(['loop', 'ping-pong']),
        everyFrames: z.number().int().positive(),
    })
        .optional(),
    origin: z.string().min(1).optional(),
    mask: motionMaskPartSchema.optional(),
});
export const exitRefSchema = z.enum(EXIT_TOKENS);
/**
 * `Indexed<AnimationToken>` — a bare token, or one per `repeat` index.
 *
 * The same primitive `styleOverride`, `container` and `glyph` already use, so
 * a row's motion varies with `$index` without a second per-row mechanism. No
 * `base`: merging two token strings is just replacement.
 */
export const indexedAnimationRef = z.union([
    animationRef,
    z.object({ byIndex: z.array(animationRef).min(1) }),
]);
export const motionSpecSchema = z.object({
    token: indexedAnimationRef.optional(),
    exit: exitRefSchema.optional(),
    durationFrames: z.number().int().positive().optional(),
    delayFrames: z.number().int().min(0).optional(),
    // Arguments to a PARAMETRIC preset. Open-keyed on purpose: the valid keys
    // are a property of the recipe the token names, and a closed union here
    // would have to be widened every time a recipe gains a knob. A recipe reads
    // only the keys it declares, so an unknown one is inert rather than wrong.
    params: z.record(z.string(), z.number()).optional(),
    tracks: z.array(motionTrackSchema).optional(),
});
/** Fully desugared motion, as it appears in a RenderDoc. No tokens survive. */
export const resolvedMotionSchema = z.object({
    startFrame: z.number().int().min(0),
    endFrame: z.number().int().min(0).optional(),
    tracks: z.array(motionTrackSchema),
});
//# sourceMappingURL=motion-schema.js.map